import { useMemo } from 'react';

import {
  featureLockConfig,
  FeatureLockContext,
  FeatureLockResult,
  LockCondition,
} from '@/config/featureLock';
import { useFlag } from '@/hooks/useFlags';
import { useGetSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/state/auth';

/**
 * Get a nested value from an object using dot notation
 *
 * @example
 * getNestedValue({ a: { b: 1 } }, 'a.b') // returns 1
 * getNestedValue({ a: { b: 1 } }, 'a.c') // returns undefined
 */
function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce((current, key) => {
    if (current && typeof current === 'object' && key in current) {
      return (current as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj as unknown);
}

/**
 * Evaluate a single condition against the context
 *
 * @returns true if condition PASSES (feature should be unlocked)
 */
function evaluateCondition(
  condition: LockCondition,
  context: FeatureLockContext
): boolean {
  let result: boolean;

  switch (condition.type) {
    case 'flag':
      result = !!context.flags[condition.value];
      break;

    case 'subscription':
      result = !!getNestedValue(context.subscription, condition.value);
      break;

    case 'userRole':
      if (condition.value === 'praetorian') {
        result = context.user.isPraetorianUser;
      } else {
        result = false;
      }
      break;

    case 'orgType':
      if (condition.value === 'demo') {
        result = context.user.isDemoOrg;
      } else {
        result = false;
      }
      break;

    default:
      result = false;
  }

  // Apply negation if specified
  if (condition.negate) {
    result = !result;
  }

  return result;
}

/**
 * Evaluate multiple conditions with the specified operator
 *
 * @returns true if conditions PASS (feature should be unlocked)
 */
function evaluateConditions(
  conditions: LockCondition | LockCondition[],
  operator: 'AND' | 'OR' = 'AND',
  context: FeatureLockContext
): boolean {
  const conditionArray = Array.isArray(conditions) ? conditions : [conditions];

  if (operator === 'OR') {
    // OR: ANY condition passing unlocks the feature
    return conditionArray.some(condition =>
      evaluateCondition(condition, context)
    );
  }

  // AND: ALL conditions must pass to unlock the feature
  return conditionArray.every(condition =>
    evaluateCondition(condition, context)
  );
}

/**
 * Hook to check if a feature is locked based on centralized configuration
 *
 * Features are gated by conditions defined in featureLockConfig.
 * Supports multiple condition types (flag, subscription, userRole, orgType)
 * with AND/OR operators and negation.
 *
 * @param featureId - The feature identifier to check (e.g., 'agents', 'attackPaths')
 * @returns FeatureLockResult with locked status, messages, and loading state
 *
 * @example
 * ```tsx
 * const { locked, lockTitle, lockDescription, isLoading } = useFeatureLock('agents');
 *
 * if (isLoading) return <Loading />;
 *
 * return (
 *   <FeatureLock
 *     locked={locked}
 *     title={lockTitle}
 *     description={lockDescription}
 *   >
 *     <AgentsContent />
 *   </FeatureLock>
 * );
 * ```
 */
export function useFeatureLock(featureId: string): FeatureLockResult {
  // Gather all context from hooks
  const {
    enableBreachAndAttack,
    disableAIMLTraining,
    enableCloud,
    enableCode,
    enableUsers,
    enableDevice,
    enableExternal,
    enablePersonalTenancy,
    isFlagLoading,
  } = useFlag();

  const { status: subscriptionStatus, data: subscriptionData } =
    useGetSubscription();

  const { isPraetorianUser, isDemoOrg } = useAuth();

  // Determine loading state
  const isLoading = isFlagLoading || subscriptionStatus === 'pending';

  // Build context object for condition evaluation
  const context: FeatureLockContext = useMemo(
    () => ({
      flags: {
        enableBreachAndAttack,
        disableAIMLTraining,
        enableCloud,
        enableCode,
        enableUsers,
        enableDevice,
        enableExternal,
        enablePersonalTenancy,
      },
      subscription: subscriptionData
        ? {
            attackSurface: subscriptionData.attackSurface,
            numberOfApplications: subscriptionData.numberOfApplications,
          }
        : {},
      user: {
        isPraetorianUser,
        isDemoOrg,
      },
    }),
    [
      enableBreachAndAttack,
      disableAIMLTraining,
      enableCloud,
      enableCode,
      enableUsers,
      enableDevice,
      enableExternal,
      enablePersonalTenancy,
      subscriptionData,
      isPraetorianUser,
      isDemoOrg,
    ]
  );

  // Look up feature config
  const config = featureLockConfig[featureId];

  // Evaluate lock status
  const result = useMemo((): FeatureLockResult => {
    // Unknown feature: return unlocked (no gating)
    if (!config) {
      return {
        locked: false,
        lockTitle: undefined,
        lockDescription: undefined,
        isLoading: false,
        featureName: undefined,
      };
    }

    // Loading state: fail-safe to locked
    // Note: Bypasses are NOT checked during loading for security (fail-safe)
    if (isLoading) {
      return {
        locked: true,
        lockTitle: config.lockTitle,
        lockDescription: config.lockDescription,
        isLoading: true,
        featureName: config.featureName,
      };
    }

    // Check bypasses first (any bypass unlocks)
    // Bypasses are checked AFTER loading state for fail-safe security
    if (config.bypasses?.length) {
      const bypassPassed = config.bypasses.some(bypass =>
        evaluateCondition(bypass, context)
      );
      if (bypassPassed) {
        return {
          locked: false,
          isLoading: false,
          featureName: config.featureName,
        };
      }
    }

    // Evaluate main conditions
    const conditionsPassed = evaluateConditions(
      config.conditions,
      config.operator,
      context
    );

    // Conditions passed = unlocked, conditions failed = locked
    const locked = !conditionsPassed;

    return {
      locked,
      lockTitle: config.lockTitle,
      lockDescription: config.lockDescription,
      isLoading: false,
      featureName: config.featureName,
    };
  }, [config, isLoading, context]);

  return result;
}
